// Messages au support (D-082). Les règles sont dans la base : l'auteur écrit et relit les siens,
// les admins de la plateforme lisent tout et répondent (support_reply).
import { supabase } from './supabase';
import { cleanVersion, type AvisOthers, type AvisTopic, type SupportKind, type SupportStatus } from '../help/support';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};

export interface SupportMessage {
  id: string; created_at: string; kind: SupportKind; body: string; page: string | null; app_version: string | null;
  user_agent: string | null; casquette: string | null; status: SupportStatus; reply: string | null; replied_at: string | null; updated_at: string;
  /** « 💬 Ton avis » (D-103) : le sujet, « autres clubs », la page en mots ; null pour un message au support. */
  topic?: AvisTopic | null; for_other_clubs?: AvisOthers | null; page_label?: string | null;
}
export interface InboxRow extends SupportMessage {
  user_id: string; has_screenshot: boolean; author_email: string | null; author_name: string | null; club_name: string | null;
}
export interface SupportDraft {
  club_id: string | null; kind: SupportKind; body: string; page: string | null; app_version: string | null;
  user_agent: string | null; casquette: string | null; screenshot: string | null;
}

/** Envoie un message ; l'erreur garde son code (54000 : limite du jour atteinte). */
export async function sendSupport(d: SupportDraft): Promise<void> {
  const { error } = await db().from('support_messages').insert(d);
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
}

/** Mes messages (un admin de la plateforme lit tout : on filtre sur l'auteur). */
export async function mySupportMessages(uid: string): Promise<SupportMessage[]> {
  const { data, error } = await db().from('support_messages')
    .select('id, created_at, kind, body, page, app_version, user_agent, casquette, status, reply, replied_at, updated_at, topic, for_other_clubs, page_label')
    .eq('user_id', uid).order('created_at', { ascending: false }).limit(50);
  if (error) throw new Error(error.message);
  return (data ?? []) as SupportMessage[];
}

let adminCheck: { uid: string; p: Promise<boolean> } | null = null;
/** Admin de la plateforme (D-081) ? Demandé une fois par compte. */
export function isPlatformAdmin(uid: string): Promise<boolean> {
  if (adminCheck?.uid !== uid) {
    adminCheck = { uid, p: Promise.resolve(db().rpc('is_platform_admin')).then(r => r.data === true, () => false) };
  }
  return adminCheck.p;
}

/** Boîte du support (vide si l'on n'est pas admin de la plateforme), sans les captures. */
export async function supportInbox(): Promise<InboxRow[]> {
  const { data, error } = await db().rpc('support_inbox', { p_status: null });
  if (error) throw new Error(error.message);
  return (data ?? []) as InboxRow[];
}

/** La capture d'un message (data URL), à part : jusqu'à 700 Ko. */
export async function supportScreenshot(id: string): Promise<string | null> {
  const { data, error } = await db().from('support_messages').select('screenshot').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return (data as { screenshot: string | null } | null)?.screenshot ?? null;
}

/** Changer l'état et/ou répondre (reply null : inchangée ; '' : retirée). L'auteur est prévenu d'une réponse. */
export async function supportReply(id: string, status: SupportStatus | null, reply: string | null): Promise<void> {
  const { error } = await db().rpc('support_reply', { p_id: id, p_status: status, p_reply: reply });
  if (error) throw new Error(error.message);
}

let versionP: Promise<string | null> | null = null;
/** La version en ligne (fichier version.txt posé à chaque mise en ligne) ; null en développement. */
export function appVersion(): Promise<string | null> {
  versionP ??= fetch('/version.txt', { cache: 'no-store', headers: { Accept: 'text/plain' } })
    .then(r => (r.ok && !/html/i.test(r.headers.get('content-type') ?? '') ? r.text() : null))
    .then(cleanVersion, () => null);
  return versionP;
}
